import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
// import AccountCircle from '@mui/icons-material/AccountCircle';
import ViewCarousel from '@mui/icons-material/ViewCarousel';
import History from '@mui/icons-material/History';
import Account from './Account/Account';
import SessionHistory from './SessionHistory/SessionHistory';
import Sessions from './Sessions/Sessions';
import Dialer from './Dialer/Dialer';
import SessionView from './SessionView/SessionView';
import Notifications from './Notifications/Notifications';
import SessionNotifications from './Notifications/SessionNotifications';
import MediaPermission from './MediaPermission';
import Avatar from '@mui/material/Avatar';

const Root = styled('div')(({ theme }) => ({
	width: '100%',
	height: '100%',
	position: 'relative'
}));

const Grow = styled('div')({
	flexGrow: 1
});

const Title = styled(Typography)(({ theme }) => ({
	display: 'none',
	[theme.breakpoints.up('sm')]: {
		display: 'block'
	}
}));

// Modified to always display regardless of screen size
const HeaderButtons = styled('div')({
	display: 'flex'
});

const WorkflowTestBadge = styled(Typography)({
	fontSize: '0.72rem',
	letterSpacing: '0.06em',
	padding: '4px 8px',
	borderRadius: '999px',
	border: '1px solid rgba(255,255,255,0.45)',
	backgroundColor: 'rgba(0,0,0,0.35)',
	color: '#fff',
	marginRight: '10px'
});

const DialerContainer = styled('div')({
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	zIndex: 1
});

const App = () => {
	const sessions = useSelector(state => state.sessions);
	const sessionCount = Object.keys(sessions).length;
	const currentSessionId = useSelector(state => state.userStatus.currentSession);
	const currentSession = currentSessionId ? sessions[currentSessionId] : null;
	const workflowTestMarkerDev2 = 'WF TEST BUILD 2026-03-31 Dev2 to Prod';
	const workflowTestMarker = 'WF TEST BUILD 2026-03-31 Dev1 to Prod' ;
	
	const [anchorEl, setAnchorEl] = useState(null);
	const [currentMenu, setCurrentMenu] = useState(null);
	const [showDialer, setShowDialer] = useState(true);
	const avatarImageUri = useSelector(state => state.user.avatarImageUri);
	// const displayName = useSelector(state => state.user.displayName);
	// Determine if we should show the dialer or session view
	useEffect(() => {
		// If there's a current session with a remote stream (media flowing), don't show dialer
		if (currentSession && currentSession.remoteStream) {
			setShowDialer(false);
		}
		// If there are no sessions, show dialer
		else if (sessionCount === 0) {
			setShowDialer(true);
		}
	}, [sessions, sessionCount, currentSession]);

	const handleExited = () => {
		setCurrentMenu(null);
	};

	const handleMenuOpen = (event, menu) => {
		setAnchorEl(event.currentTarget);
		setCurrentMenu(menu);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const isMenuOpen = Boolean(anchorEl);

	return (
		<Root>
			<SessionView />
			<Notifications />
			<SessionNotifications />
			<MediaPermission />
			{showDialer && (
				<DialerContainer>
					<Dialer />
				</DialerContainer>
			)}
			<AppBar
				position='static'
				sx={{ background: 'transparent', boxShadow: 'none' }}
			>
				<Toolbar>
					<Title
						variant='h6'
						color='inherit'
						noWrap
					>
						SipCaller 1.2.7
					</Title>
					<Grow />
					<WorkflowTestBadge variant='body2'>
						{workflowTestMarkerDev2}
						{workflowTestMarker}
					</WorkflowTestBadge>
					<HeaderButtons>
						<IconButton
							aria-owns={isMenuOpen && currentMenu === 'sessions' ? 'material-appbar' : undefined}
							aria-haspopup='true'
							onClick={(event) => handleMenuOpen(event, 'sessions')}
							color='inherit'
							size='large'
						>
							<Badge badgeContent={sessionCount} color='secondary'>
								<ViewCarousel />
							</Badge>
						</IconButton>
						<IconButton
							aria-owns={isMenuOpen && currentMenu === 'history' ? 'material-appbar' : undefined}
							aria-haspopup='true'
							onClick={(event) => handleMenuOpen(event, 'history')}
							color='inherit'
							size='large'
						>
							<History />
						</IconButton>
						<IconButton
							aria-owns={isMenuOpen && currentMenu === 'account' ? 'material-appbar' : undefined}
							aria-haspopup='true'
							onClick={(event) => handleMenuOpen(event, 'account')}
							color='inherit'
							size='large'
							>
							<Avatar
								src={avatarImageUri || undefined}
								// alt={displayName || 'User'}
								sx={{
								width: 32,
								height: 32,
								border: '2px solid rgba(255,255,255,0.35)'
								}}
							>
								{/* fallback: first letter if no image */}
								{/* {(displayName?.[0] || 'U').toUpperCase()} */}
							</Avatar>
						</IconButton>
					</HeaderButtons>
				</Toolbar>
			</AppBar>
			<Popover
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				open={isMenuOpen}
				onClose={handleMenuClose}
				onExited={handleExited}
				disableAutoFocus
			>
				{ currentMenu === 'sessions' ?
					<Sessions />
					:null
				}
				{ currentMenu === 'history' ?
					<SessionHistory />
					:null
				}
				{ currentMenu === 'account' ?
					<Account />
					:null
				}
			</Popover>
		</Root>
	);
};

export default App;
